import React, { useState, useEffect } from 'react';
import './SyllabusStep.css';
import { useAuth } from '../../../contexts/AuthContext';
import { FileApi, DocumentApi } from '../../../services/ApiService';
import { AiDocumentProcessingApi } from '../../../services/ai/AiDocumentProcessingApi';
import { DocumentSelectionModal } from '../../../components';
import { SPINNER_MESSAGES } from '../../../constants/spinnerMessages';

function SyllabusStep({
  courseData,
  setCourseData,
  processing,
  processSyllabus, // kept for backward compatibility (currently unused)
  checkingExistingEndeavors,
  onSyllabusDocumentSelected,
  onManualMode,
  parentEvent,
  events
}) {
    const { user } = useAuth();

    // UI State
    const [dragOver, setDragOver] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [localProcessing, setLocalProcessing] = useState(false);

    // Spinner overlay
    const [syllabusProcessing, setSyllabusProcessing] = useState(false);
    const [spinnerIndex, setSpinnerIndex] = useState(() => Math.floor(Math.random() * SPINNER_MESSAGES.length));
    const [spinnerMessage, setSpinnerMessage] = useState(SPINNER_MESSAGES[Math.floor(Math.random() * SPINNER_MESSAGES.length)]);

    // Advance spinner index by a random step (1-5) while processing
    useEffect(() => {
      if (!syllabusProcessing) return;
      const id = setInterval(() => {
        setSpinnerIndex(prev => (prev + (Math.floor(Math.random() * 5) + 1)) % SPINNER_MESSAGES.length);
      }, 6000);
      return () => clearInterval(id);
    }, [syllabusProcessing]);

    // Update visible message whenever index changes
    useEffect(() => {
      setSpinnerMessage(SPINNER_MESSAGES[spinnerIndex]);
    }, [spinnerIndex]);

    // Auto-apply detected recurrence pattern from syllabus preview
    useEffect(() => {
      const preview = courseData?.syllabusPreview;
      if (!preview) return;
      const recTop = preview?.recurrence_analysis || {};
      const recInner = preview?.syllabus_data?.recurrence_analysis || {};
      const detected = recTop.rrule || recTop.confidence_analysis?.detected_pattern || recInner.rrule || recInner.confidence_analysis?.detected_pattern || '';
      if (detected && !courseData.schedulePattern) {
        handleInputChange('schedulePattern', detected);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseData?.syllabusPreview]);

    const to24h = (h,m,ampm) => {
      let hh = parseInt(h,10);
      const mm = String(parseInt(m||'0',10)).padStart(2,'0');
      const mer = (ampm||'').toLowerCase();
      if (mer==='pm' && hh < 12) hh += 12;
      if (mer==='am' && hh === 12) hh = 0;
      return `${String(hh).padStart(2,'0')}:${mm}`;
    };

    const parseTimesFromString = (s) => {
      if (!s || typeof s !== 'string') return {};
      const re = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i;
      const m = s.match(re);
      if (!m) return {};
      const start = to24h(m[1], m[2], m[3]);
      const end = to24h(m[4], m[5], m[6] || m[3]);
      return { start, end };
    };

    const handleInputChange = (field, value) => setCourseData(prev => ({ ...prev, [field]: value }));

    const getQualityPercentage = () => courseData.syllabusPreview?.quality_assessment?.percentage
      ? Math.round(courseData.syllabusPreview.quality_assessment.percentage)
      : null;

    const getExtractedCounts = () => {
      if (!courseData.syllabusPreview?.syllabus_data) return null;
      const data = courseData.syllabusPreview.syllabus_data;
      const sessionCount = data.class_sessions?.length || 0;
      const standaloneAssignments = data.standalone_assignments?.length || 0;
      const sessionAssignments = data.class_sessions?.reduce((t,s)=> t + (s.assignments?.length || 0),0) || 0;
      return { sessions: sessionCount, assignments: standaloneAssignments + sessionAssignments };
    };

    // Core syllabus processing (restored)
    const handleProcessSyllabus = async () => {
      if (!courseData.syllabusDocumentId) { alert('Please select a syllabus document first.'); return; }
      setLocalProcessing(true);
      setSyllabusProcessing(true);
      setCourseData(prev => ({ ...prev, processingError:null, syllabusPreview:null }));
      try {
        const response = await AiDocumentProcessingApi.processDocumentAsSyllabus(
          courseData.syllabusDocumentId,
          { courseName: courseData.courseName || '' }
        );
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Processing failed: ${response.status} - ${errorText}`);
        }
        const result = await response.json();
        const syllabusData = result.syllabus_data;
        const topLevelRecurrence = result.recurrence_analysis || {};
        const innerRecurrence = syllabusData?.recurrence_analysis || {};
        const detected = topLevelRecurrence.rrule ||
          topLevelRecurrence.confidence_analysis?.detected_pattern ||
          innerRecurrence.rrule ||
          innerRecurrence.confidence_analysis?.detected_pattern || '';

        if (syllabusData && syllabusData.course_information) {
          const courseInfo = syllabusData.course_information;
          // Support both meeting_time and meeting_times keys
          const meetingTimeField = courseInfo.meeting_times || courseInfo.meeting_time || '';
          const sessions = Array.isArray(syllabusData.class_sessions) ? syllabusData.class_sessions : [];
          const firstSession = sessions[0] || {};

          // Use structured time fields if present, otherwise parse from text
          let derivedStart = '';
          let derivedEnd = '';
          
          if (courseInfo.meeting_start_time && courseInfo.meeting_end_time) {
            // Have structured time fields - use them directly
            derivedStart = courseInfo.meeting_start_time.slice(0, 5);
            derivedEnd = courseInfo.meeting_end_time.slice(0, 5);
          } else {
            // Fall back to parsing meeting_time text
            const mtParsed = parseTimesFromString(meetingTimeField);
            derivedStart = (mtParsed.start || (firstSession.startDate ? new Date(firstSession.startDate).toISOString().substring(11,16) : '')).slice(0,5);
            derivedEnd = (mtParsed.end || '').slice(0,5);
          }

          // Derive start / end dates from sessions if not already provided
          let derivedCourseStart = courseInfo.start_date || '';
          let derivedCourseEnd = courseInfo.end_date || '';
          if ((!derivedCourseStart || !derivedCourseEnd) && sessions.length) {
            const dateList = sessions.map(s => s.startDate).filter(Boolean).map(d => new Date(d));
            if (dateList.length) {
              const minD = dateList.reduce((a,b)=> a < b ? a : b);
              const maxD = dateList.reduce((a,b)=> a > b ? a : b);
              if (!derivedCourseStart) derivedCourseStart = minD.toISOString().slice(0,10);
              if (!derivedCourseEnd) derivedCourseEnd = maxD.toISOString().slice(0,10);
            }
          }

          // If we didn't detect an RRULE pattern, attempt to build one from meeting_time or sessions
          let derivedRRule = '';
          if (!detected) {
            // Extract days from meeting_time string
              const dayTokens = [];
              const dayMap = {
                monday: 'MO', mon: 'MO', tuesday: 'TU', tue: 'TU', tues: 'TU', tuesdays:'TU',
                wednesday: 'WE', wed: 'WE', thursday: 'TH', thu: 'TH', thurs: 'TH', thursdays:'TH',
                friday: 'FR', fri: 'FR', saturday: 'SA', sat: 'SA', sunday: 'SU', sun: 'SU'
              };
              const lower = meetingTimeField.toLowerCase();
              Object.keys(dayMap).forEach(k => { if (lower.includes(k) && !dayTokens.includes(dayMap[k])) dayTokens.push(dayMap[k]); });
              if (!dayTokens.length && sessions.length) {
                sessions.slice(0,10).forEach(s => { try { const d = new Date(s.startDate); const dow = ['SU','MO','TU','WE','TH','FR','SA'][d.getUTCDay()]; if (!dayTokens.includes(dow)) dayTokens.push(dow); } catch(_){} });
              }
              if (dayTokens.length) derivedRRule = `FREQ=WEEKLY;BYDAY=${dayTokens.join(',')};WKST=SU`;
          }

          const schedulePatternToUse = detected || derivedRRule || '';

          setCourseData(prev => ({
            ...prev,
            courseName: courseInfo.course_name || prev.courseName,
            instructor: courseInfo.instructor || courseInfo.instructor_name || prev.instructor,
            startDate: prev.startDate || derivedCourseStart || prev.startDate,
            endDate: prev.endDate || derivedCourseEnd || prev.endDate,
            location: courseInfo.location || prev.location,
            description: courseInfo.course_description || prev.description,
            semester: courseInfo.semester || prev.semester || '',
            meetingTimes: meetingTimeField || prev.meetingTimes || '',
            meetingStartTime: prev.meetingStartTime || derivedStart,
            meetingEndTime: prev.meetingEndTime || derivedEnd,
            requiredMaterials: Array.isArray(courseInfo.course_materials) ? courseInfo.course_materials.join('; ') : (courseInfo.course_materials || courseInfo.textbook || prev.requiredMaterials || ''),
            schedulePattern: prev.schedulePattern || schedulePatternToUse,
            detectedSchedulePattern: schedulePatternToUse, // for autoDetectedRRule in EventsStep
            syllabusPreview: result,
            processingError: null
          }));
        } else {
          setCourseData(prev => ({ ...prev, processingError: 'We could not extract course information from the syllabus.' }));
        }
      } catch (err) {
        setCourseData(prev => ({ ...prev, processingError: err.message || 'Failed to process syllabus.' }));
      } finally {
        setLocalProcessing(false);
        setSyllabusProcessing(false);
      }
    };

    // File upload
    const handleFileUpload = async (file) => {
      try {
        setUploading(true);
        const metadata = { name: file.name, documentTypeId:4, description: `Syllabus for ${courseData.courseName || 'course'}`, userId: user.user_id || user.userId || user.id };
        const response = await FileApi.uploadFile(file, metadata);
        if (!response.ok) { const errorText = await response.text(); throw new Error(`Upload failed: ${response.status} - ${errorText}`); }
        const uploadData = await response.json();
        const documentId = uploadData.documentId || uploadData.id || uploadData.document?.documentId || uploadData.document?.id;
        setCourseData(prev => ({ ...prev, syllabusFile: file, syllabusDocumentId: documentId, selectedSyllabusDocument: { id: documentId, name: file.name, ...uploadData }, syllabusPreview:null, processingError:null }));
        if (onSyllabusDocumentSelected) { try { onSyllabusDocumentSelected(documentId); } catch {} }
        // Background full processing (non-blocking): fire & forget, log outcome
        ;(async () => {
          try {
            console.log('[SyllabusStep] Starting background full processing for document', documentId);
            const fullResp = await AiDocumentProcessingApi.processDocumentFull(documentId, { courseName: courseData.courseName || undefined });
            if (fullResp?.ok) {
              console.log('[SyllabusStep] Background full processing completed for document', documentId);
            } else {
              console.warn('[SyllabusStep] Background full processing response not ok', fullResp?.status);
            }
          } catch (bgErr) {
            console.warn('[SyllabusStep] Background full processing failed:', bgErr?.message || bgErr);
          }
        })();
      } catch (e) {
        alert(`Failed to upload file: ${e.message}`);
      } finally { setUploading(false); }
    };

    const handleFileInput = (e) => { const files = Array.from(e.target.files); if (files.length) handleFileUpload(files[0]); };
    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const files = Array.from(e.dataTransfer.files); if (files.length) handleFileUpload(files[0]); };

    const handleDocumentSelection = async (selectedDocs) => {
      if (!selectedDocs.length) return; const documentId = selectedDocs[0];
      try {
        const response = await DocumentApi.getDocument(documentId);
        if (!response.ok) { const errText = await response.text(); throw new Error(`Failed to fetch document: ${response.status} - ${errText}`); }
        const document = await response.json();
        setCourseData(prev => ({ ...prev, syllabusFile:null, syllabusDocumentId:documentId, selectedSyllabusDocument:document, syllabusPreview:null, processingError:null }));
        setShowDocumentModal(false); setSelectedDocuments([]);
        if (onSyllabusDocumentSelected) { try { onSyllabusDocumentSelected(documentId); } catch {} }
        // Fire & forget full processing for selected existing document
        ;(async () => {
          try {
            console.log('[SyllabusStep] Starting background full processing for existing document', documentId);
            const fullResp = await AiDocumentProcessingApi.processDocumentFull(documentId, { courseName: courseData.courseName || undefined });
            if (fullResp?.ok) {
              console.log('[SyllabusStep] Background full processing completed for existing document', documentId);
            } else {
              console.warn('[SyllabusStep] Background processing response not ok', fullResp?.status);
            }
          } catch (bgErr) {
            console.warn('[SyllabusStep] Background processing failed for existing document:', bgErr?.message || bgErr);
          }
        })();
      } catch (e) { alert('Failed to select document.'); }
    };

    const toggleDocumentSelection = (docId) => setSelectedDocuments([docId]);
    const handleClearSelection = () => setCourseData(prev => ({ ...prev, syllabusFile:null, syllabusDocumentId:null, selectedSyllabusDocument:null, syllabusPreview:null, processingError:null }));

    const isAnyLoading = uploading || processing || localProcessing || checkingExistingEndeavors;
    const isSyllabusProcessed = courseData.syllabusPreview && !courseData.processingError;

    // --- RENDER ---
    return (
      <div className="syllabus-step">
        <div className="step-content">
          {/* Step intro heading */}
          <div className="step-intro">
            <h2 className="step-intro-title">Set up your course</h2>
            <p className="step-intro-desc">Upload your syllabus and we'll automatically extract your course details, schedule, and assignments.</p>
          </div>

            {/* ──── Upload Card ──── */}
            <div className="upload-section" data-tour="syllabus-upload">
              <div className="section-header">
                <div className="icon-chip"><span className="icon-chip-emoji">📄</span></div>
                <div className="header-text">
                  <h3>Syllabus Document</h3>
                  <p className="section-subtitle">Upload or select your course syllabus to get started.</p>
                </div>
              </div>

              <div className="syllabusCardBody">
                {courseData.syllabusDocumentId ? (
                  /* ─ File Selected State ─ */
                  <div className="current-selection">
                    <div className="file-pill-row">
                      <div className="file-pill">
                        <span className="file-pill-icon">📎</span>
                        <span className="file-pill-name">{courseData.selectedSyllabusDocument?.name || courseData.syllabusFile?.name || 'Selected document'}</span>
                      </div>
                      <button className="btn-change-file" onClick={handleClearSelection} disabled={isAnyLoading}>Change file</button>
                    </div>

                    {/* Unprocessed — show scan CTA */}
                    {!isSyllabusProcessed && !localProcessing && !courseData.processingError && (
                      <div className="process-cta-card">
                        <div className="process-cta-header">
                          <span className="process-cta-icon">🔍</span>
                          <div className="process-cta-text">
                            <strong>Ready to scan</strong>
                            <p>We'll extract course info, schedule, and assignments from your syllabus.</p>
                          </div>
                        </div>
                        <button className="btn-primary process-syllabus-btn" onClick={handleProcessSyllabus} disabled={isAnyLoading}>Scan Syllabus</button>
                      </div>
                    )}

                    {/* Processing in-progress */}
                    {(processing || localProcessing) && (
                      <div className="processing-status processing">
                        <div className="status-icon"><span className="mini-spinner" /></div>
                        <div className="status-text">
                          <strong>Scanning your syllabus…</strong>
                          <p>Extracting course info, schedule & assignments. This may take a minute or two.</p>
                        </div>
                      </div>
                    )}

                    {/* Successfully processed */}
                    {isSyllabusProcessed && (
                      <div className="processing-status success">
                        <div className="status-icon">✅</div>
                        <div className="status-text">
                          <strong>Syllabus scanned successfully</strong>
                          <p>
                            {getQualityPercentage() && <>{getQualityPercentage()}% confidence · </>}
                            {getExtractedCounts() && <>{getExtractedCounts().sessions} sessions · {getExtractedCounts().assignments} assignments found</>}
                          </p>
                          <p className="help-text"><em>Review your course details below and create your course.</em></p>
                        </div>
                      </div>
                    )}

                    {/* Error state */}
                    {courseData.processingError && (
                      <div className="processing-status error">
                        <div className="status-icon">⚠️</div>
                        <div className="status-text">
                          <strong>Scan failed</strong>
                          <p>{courseData.processingError}</p>
                          <button className="btn-secondary btn-sm" onClick={handleProcessSyllabus} disabled={isAnyLoading}>Try again</button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ─ Empty Upload State ─ */
                  <div className="upload-content">
                    <div className={`dropzone ${dragOver ? 'drag-over' : ''}`} onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}>
                      <div className="upload-icon-wrapper">
                        <span className="upload-icon">📤</span>
                      </div>
                      <p className="dropzone-heading">Drag & drop your syllabus here</p>
                      <p className="dropzone-subtext">or choose from the options below</p>
                      <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleFileInput} style={{ display: 'none' }} id="syllabus-upload" disabled={isAnyLoading} />
                      <div className="syllabusPrimaryActions">
                        <label htmlFor="syllabus-upload" className="syl-btn-choose">{uploading ? 'Uploading…' : '📁 Choose File'}</label>
                        <button className="syl-btn-existing" onClick={() => setShowDocumentModal(true)} disabled={isAnyLoading}>📚 Use Existing</button>
                      </div>
                      <div className="file-type-badges">
                        <span className="badge">PDF</span>
                        <span className="badge">DOCX</span>
                        <span className="badge">TXT</span>
                      </div>
                    </div>

                    {/* ── Or Create Manually divider ── */}
                    {onManualMode && (
                      <div className="manual-divider">
                        <span className="manual-divider-line" />
                        <span className="manual-divider-text">or</span>
                        <span className="manual-divider-line" />
                      </div>
                    )}
                    {onManualMode && (
                      <button type="button" className="syl-btn-manual" onClick={onManualMode}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                        Create Course Manually
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
        </div>

        <DocumentSelectionModal
          isOpen={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          onConnectDocuments={handleDocumentSelection}
          selectedDocuments={selectedDocuments}
          onToggleDocument={toggleDocumentSelection}
          documentTypeId={4}
          title="Select a Syllabus"
          description="Choose a previously uploaded syllabus document."
        />

        {syllabusProcessing && (
          <div className="syllabus-processing-overlay" role="alert" aria-live="polite">
            <div className="syllabus-processing-panel">
              <div className="syllabus-spinner" />
              <p className="spinner-line">{spinnerMessage}</p>
              <p className="hint">Analyzing your syllabus — this usually takes 1-3 minutes.</p>
            </div>
          </div>
        )}
      </div>
    );
}

export default SyllabusStep;
